import { useCustomArticlesDefaults } from "@tm/data/basket"
import { Checkbox, CurrencyField, Icon, MenuItem, SelectTextField, Stack, TextField, Typography } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { Controller, useFormContext } from "react-hook-form"
import { useArticleFormDefaults } from "@bundles/basket/hooks/Forms/useArticleFormDefaults"
import { errorHandling } from "@tm/utils"
import { z } from "zod"
import { useSchema } from "./schema"

type Props = {
    categoryId: number
}

const MAX_COST = 9999.99

export function EditDialogBody({ categoryId }: Props) {
    const { translateText } = useLocalization()
    const schema = useSchema()
    const { control } = useFormContext<z.infer<typeof schema>>()
    const { vatRateItems } = useArticleFormDefaults()
    const { customArticlesDefaults: { defaultCurrencyCode } = {} } = useCustomArticlesDefaults()

    return (
        <Stack spacing={1}>
            <Stack direction="row" gap={1}>
                <Controller
                    control={control}
                    name="articleNumber"
                    render={({ field, fieldState }) => (
                        <TextField
                            label={translateText(87).toUpperCase()}
                            size="extralarge"
                            sx={{ width: "235px" }}
                            {...field}
                            {...errorHandling(fieldState)}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="description"
                    render={({ field, fieldState }) => (
                        <TextField
                            required
                            label={translateText(25).toUpperCase()}
                            size="extralarge"
                            sx={{ width: "435px" }}
                            {...field}
                            {...errorHandling(fieldState)}
                        />
                    )}
                />
            </Stack>
            <Stack direction="row" gap={1}>
                <TextField
                    id="category"
                    label={translateText(158).toUpperCase()}
                    value={translateText(categoryId)}
                    size="extralarge"
                    sx={{ width: "400px" }}
                    disabled
                />
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <Controller
                        control={control}
                        name="isDefaultForCategory"
                        render={({ field }) => <Checkbox checked={!!field.value} disabled={!categoryId || categoryId === 3060} {...field} />}
                    />
                    <Icon name="category-bestseller" />
                    <Typography variant="label">{translateText(12702)}</Typography>
                </Stack>
                <Controller
                    control={control}
                    name="purchasePrice"
                    render={({ field, fieldState }) => (
                        <CurrencyField
                            label={`${translateText(1132)} ${defaultCurrencyCode}`}
                            size="extralarge"
                            variant="filled"
                            sx={{ width: "140px" }}
                            max={MAX_COST}
                            {...field}
                            {...errorHandling(fieldState)}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="retailPrice"
                    render={({ field, fieldState }) => (
                        <CurrencyField
                            label={`${translateText(1257)} ${defaultCurrencyCode}`}
                            size="extralarge"
                            variant="filled"
                            sx={{ width: "140px" }}
                            max={MAX_COST}
                            {...field}
                            {...errorHandling(fieldState)}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="vatType"
                    render={({ field, fieldState }) => (
                        <SelectTextField
                            label={translateText(706)}
                            size="extralarge"
                            sx={{ width: "250px" }}
                            {...field}
                            {...errorHandling(fieldState)}
                        >
                            {vatRateItems.map(({ vatRate, text }) => (
                                <MenuItem key={vatRate.vatType} value={vatRate.vatType}>
                                    {text}
                                </MenuItem>
                            ))}
                        </SelectTextField>
                    )}
                />
            </Stack>
            <Controller
                control={control}
                name="note"
                render={({ field, fieldState }) => (
                    <TextField
                        size="extralarge"
                        multiline
                        label={translateText(39)}
                        fullWidth
                        inputCounter
                        minRows={3}
                        inputProps={{
                            maxLength: 500,
                        }}
                        {...field}
                        {...errorHandling(fieldState)}
                    />
                )}
            />
        </Stack>
    )
}
